import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, ConfigProvider, Layout, Menu /*theme*/ } from 'antd';
const { Sider, Content } = Layout;
import california_commons_logo from '../../images/toBeUpdated.png';
import { FaChartSimple, FaMap } from 'react-icons/fa6';
import { FiUsers } from 'react-icons/fi';
import { MdMenuBook, MdFolderShared } from 'react-icons/md';
import './header.css';
import { FaBars, FaGlobe, FaHome, FaTimes } from 'react-icons/fa';

const routeToOpenKeys = {
  '/documentation': ['documentation'],
  '/documentation/features': ['documentation', 'documentation-features'],
  '/documentation/catalog': ['documentation', 'documentation-catalog'],
  '/documentation/catalog/search': ['documentation', 'documentation-catalog'],
  '/documentation/catalog/download': ['documentation', 'documentation-catalog'],
  '/documentation/mapviewer': ['documentation', 'documentation-mapviewer'],
  '/documentation/mapviewer/dataset': [
    'documentation',
    'documentation-mapviewer',
  ],
};

// Below this width the sidebar becomes a slide-in menu so pages can reflow to
// 320px (WCAG 1.4.10).
const MOBILE_QUERY = '(max-width: 767.98px)';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

export default function ForestDataHubHeader({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [openKeys, setOpenKeys] = useState([]);

  useEffect(() => {
    setOpenKeys(
      (routeToOpenKeys[location.pathname] ??
        location.pathname.includes('documentation'))
        ? ['documentation']
        : []
    );
  }, [location.pathname]);

  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);

  const closeMenu = () => {
    setMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  // Close the mobile menu after navigating, and when leaving mobile width.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, isMobile]);

  // Opening the menu moves focus into it; Escape closes it.
  useEffect(() => {
    if (!menuOpen) return;
    document
      .getElementById('site-sidebar')
      ?.querySelector('button, [href], [role="menuitem"]')
      ?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  return (
    <Layout>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      {isMobile && menuOpen && (
        // Clicking outside the open menu closes it. Keyboard users have
        // Escape and the Menu button, so this stays out of the tab order.
        <div
          className="mobile-menu-backdrop"
          aria-hidden="true"
          onClick={closeMenu}
        />
      )}
      <Sider
        id="site-sidebar"
        className={
          'site-sidebar' + (isMobile && menuOpen ? ' site-sidebar-open' : '')
        }
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={300}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          backgroundColor: '#2C3E37',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <nav aria-label="Main navigation" style={{ height: '100vh' }}>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <span>
              <Button
                className="logo-button"
                aria-label="California Wildfire Commons — go to home page"
                onClick={() => navigate('/')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'white',
                  height: '170px',
                  borderWidth: 0,
                  borderRadius: 0,
                  padding: '12px 0',
                  width: '100%',
                }}
              >
                <div className="logo" style={{ width: '100%', padding: 0 }}>
                  <img
                    height={150}
                    src={california_commons_logo}
                    alt="California Wildfire Commons logo"
                  />
                </div>
              </Button>
              <ConfigProvider
                theme={{
                  components: {
                    Menu: {
                      subMenuItemBg: '#2C3E37',
                      darkSubMenuItemBg: '#2C3E37',
                      fontSize: 16,
                      itemHeight: 44,
                      itemPaddingInline: 12,
                      darkItemColor: 'white',
                      darkItemHoverColor: '#e3b448',
                      darkItemSelectedColor: '#F2C55D',
                      darkItemBg: '#e3b448',
                      darkItemSelectedBg: '#664A00',
                    },
                  },
                }}
              >
                <Menu
                  theme="dark"
                  mode="inline"
                  selectedKeys={[location.pathname]}
                  openKeys={openKeys}
                  onOpenChange={setOpenKeys}
                  onClick={({ key }) => {
                    if (key.startsWith('http')) {
                      window.open(key, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate(key);
                    }
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: '#2C3E37',
                  }}
                  aria-label="Site navigation"
                  items={[
                    {
                      key: '/',
                      icon: <FaHome />,
                      label: 'Welcome',
                    },
                    { key: '/about', icon: <FiUsers />, label: 'About' },
                    {
                      key: '/data_collection',
                      icon: <FaChartSimple />,
                      label: 'Data Collections',
                    },
                    {
                      key: '/catalog',
                      icon: <MdMenuBook />,
                      label: 'Catalog',
                    },

                    {
                      key: '/mapviewer',
                      icon: <FaMap />,
                      label: 'Map Viewer',
                    },

                    {
                      key: '/workspace',
                      icon: <MdFolderShared />,
                      label: 'Published Workspaces',
                    },
                    {
                      key: 'https://caregionalresourcekits.org/clm.html',
                      icon: <FaGlobe />,
                      label: 'CLM Website',
                    },
                    {
                      key: 'https://wildfiretaskforce.org/southern-california-ignition-reduction-program/',
                      icon: <FaGlobe />,
                      label: 'SCIRP Website',
                    },
                    {
                      key: 'https://interagencytrackingsystem.org/',
                      icon: <FaGlobe />,
                      label: 'ITS Dashboard',
                    },
                    {
                      key: 'https://app.planscape.org/home',
                      icon: <FaGlobe />,
                      label: 'Planscape App',
                    },
                    {
                      key: 'documentation',
                      // icon: <UploadOutlined />,
                      label: (
                        <span style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                          Getting Started
                        </span>
                      ),

                      // type: 'group',
                      children: [
                        {
                          type: 'divider',
                          className: 'submenudivider',
                        },
                        { key: '/documentation', label: 'Home' },
                        {
                          key: 'documentation-features',
                          label: 'Overview',
                          // type: 'group',
                          children: [
                            {
                              key: '/documentation/features',
                              label: (
                                <span className="submenu-child">
                                  Core Features
                                </span>
                              ),
                            },
                          ],
                        },
                        {
                          key: '/documentation/gettingstarted',
                          label: 'Get Started',
                        },
                        {
                          key: 'documentation-catalog',
                          label: 'Use the Catalog',
                          children: [
                            {
                              key: '/documentation/catalog/search',
                              label: (
                                <span className="submenu-child">
                                  Search the Catalog
                                </span>
                              ),
                            },
                            {
                              key: '/documentation/catalog/download',
                              label: (
                                <span className="submenu-child">
                                  Download from the Catalog
                                </span>
                              ),
                            },
                          ],
                        },
                        {
                          key: 'documentation-mapviewer',
                          label: 'Use the Map Viewer',
                          children: [
                            {
                              key: '/documentation/mapviewer/dataset',
                              label: (
                                <span className="submenu-child">
                                  Visualization Datasets
                                </span>
                              ),
                            },
                            {
                              key: '/documentation/mapviewer/download',
                              label: (
                                <span className="submenu-child">
                                  Downloading Map Data
                                </span>
                              ),
                            },
                            {
                              key: '/documentation/mapviewer/its',
                              label: (
                                <span className="submenu-child">
                                  Generate ITS report
                                </span>
                              ),
                            },
                          ],
                        },
                        // {
                        //   key: 'documentation-collections',
                        //   label: 'Data Collections',
                        //   children: [
                        //     {
                        //       key: '/documentation/collections/clm',
                        //       label: (
                        //         <span className="submenu-child">
                        //           California Landscape Metrics
                        //         </span>
                        //       ),
                        //     },
                        //     {
                        //       key: '/documentation/collections/its',
                        //       label: (
                        //         <span className="submenu-child">
                        //           Interagency Tracking System
                        //         </span>
                        //       ),
                        //     },
                        //   ],
                        // },
                        {
                          key: '/documentation/mappingtools',
                          label: 'Third-Party Integrations',
                        },
                        // { key: '/documentation/support', label: 'Support' },
                      ],
                    },
                  ]}
                />
              </ConfigProvider>
            </span>
            <div
              style={{
                bottom: '10px',
                padding: '12px',
                borderTop: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <a
                href="https://www.wildfirecommons.org/"
                style={{ color: 'white', fontWeight: 500 }}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Back to Wildfire Commons (opens in new tab)"
              >
                Back to Wildfire Commons
              </a>
            </div>
          </div>
        </nav>
      </Sider>
      <Layout>
        {isMobile && (
          <div className="mobile-topbar">
            <button
              type="button"
              ref={menuButtonRef}
              className="mobile-menu-button"
              aria-expanded={menuOpen}
              aria-controls="site-sidebar"
              onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
            >
              {menuOpen ? (
                <FaTimes aria-hidden="true" />
              ) : (
                <FaBars aria-hidden="true" />
              )}
              Menu
            </button>
            <span className="mobile-topbar-title">
              California Wildfire Commons
            </span>
          </div>
        )}
        <Content className="site-content">
          <main
            id="main-content"
            tabIndex={-1}
            style={{ outline: 'none', height: '100%' }}
          >
            {children}
          </main>
        </Content>
      </Layout>
    </Layout>
  );
}
